/**
 * Checks if sales are active based on dates
 * @param startDate - The start date of the sales period
 * @param endDate - The end date of the sales period
 * @returns boolean - True if both dates are set and current date is within the period
 */
export const isSalesActiveByDates = (startDate: string | null | undefined, endDate: string | null | undefined): boolean => {
  // Both dates must be set for a valid sales period
  if (!startDate || !endDate) return false;

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  return today >= start && today <= end;
};

/**
 * Checks if a team has active sales
 * @param team - The team object containing sales dates
 * @returns boolean - True if sales are active within date period
 */
export const isTeamSalesActive = (team: { salesStartDate?: string | null; salesEndDate?: string | null } | null | undefined): boolean => {
  return isSalesActiveByDates(team?.salesStartDate, team?.salesEndDate);
};

/**
 * Checks if an association has active sales
 * @param association - The association object containing sales dates
 * @returns boolean - True if sales are active within date period
 */
export const isAssociationSalesActive = (association: { salesStartDate?: string | null; salesEndDate?: string | null } | null | undefined): boolean => {
  return isSalesActiveByDates(association?.salesStartDate, association?.salesEndDate);
};

/**
 * Checks if webshop should be accessible
 * - Allows access before sales start
 * - Blocks access after sales end
 * @param endDate - The end date of the sales period
 * @returns boolean - True if webshop should be accessible
 */
export const isWebshopAccessibleForOrder = (endDate: string | null | undefined): boolean => {
  // If no end date is set, allow access (sales haven't ended)
  if (!endDate) return true;

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  // Block access only if we're past the end date
  return today <= end;
}; 
